import { EntityBase } from '@provision/entity-types';
import dayjs from 'dayjs';
import { isNullish } from 'remeda';
import { ConditionInput } from 'src/graphql/types';

/**
 * Helper to check if a value is a valid date string.
 */
function isDate(val: unknown): val is string {
  return typeof val === 'string' && dayjs(val).isValid();
}

/**
 * `isEntityRelevantToFilters` attempts to determine potential relevance based on event attributes
 * and query filters.
 *
 * Note that this check is not exhaustive—full-scale filter evaluation
 * (to perfectly mirror backend filtering) is not feasible on the client side. Instead,
 * a simplified attribute comparison is performed.
 */
export function isEntityRelevantToFilters(
  entityAttributes: EntityBase,
  filters?: ConditionInput[],
): boolean {
  if (!filters || filters.length === 0) return true;

  return filters.every((filter) => {
    const eventValue = entityAttributes[filter.attribute as keyof EntityBase];
    const filterValue = filter.value;

    switch (filter.operator) {
      case '=':
        if (isDate(eventValue) && isDate(filterValue)) {
          return dayjs(eventValue).isSame(dayjs(filterValue));
        }
        return String(eventValue) === String(filterValue);

      case '!=':
        if (isDate(eventValue) && isDate(filterValue)) {
          return !dayjs(eventValue).isSame(dayjs(filterValue));
        }
        return String(eventValue) !== String(filterValue);

      case 'is_empty':
      case 'is_null':
        return isNullish(eventValue);

      case 'is_not_empty':
      case 'is_not_null':
        return !isNullish(eventValue);

      case 'ilike':
        return (
          typeof eventValue === 'string' &&
          typeof filterValue === 'string' &&
          eventValue.toLowerCase().includes(filterValue.toLowerCase())
        );

      case 'not_ilike':
        return (
          typeof eventValue === 'string' &&
          typeof filterValue === 'string' &&
          !eventValue.toLowerCase().includes(filterValue.toLowerCase())
        );

      case '<': {
        if (isDate(eventValue) && isDate(filterValue)) {
          return dayjs(eventValue).isBefore(dayjs(filterValue));
        }
        return eventValue < filterValue;
      }

      case '>': {
        if (isDate(eventValue) && isDate(filterValue)) {
          return dayjs(eventValue).isAfter(dayjs(filterValue));
        }
        return eventValue > filterValue;
      }

      case '<=': {
        if (isDate(eventValue) && isDate(filterValue)) {
          const eventDate = dayjs(eventValue);
          const filterDate = dayjs(filterValue);
          return eventDate.isBefore(filterDate) || eventDate.isSame(filterDate);
        }
        return eventValue <= filterValue;
      }

      case '>=': {
        if (isDate(eventValue) && isDate(filterValue)) {
          const eventDate = dayjs(eventValue);
          const filterDate = dayjs(filterValue);
          return eventDate.isAfter(filterDate) || eventDate.isSame(filterDate);
        }
        return eventValue >= filterValue;
      }

      case 'between': {
        const values = String(filterValue).split(',');
        if (values.length === 2) {
          if (isDate(eventValue) && dayjs(values[0]).isValid() && dayjs(values[1]).isValid()) {
            const eventDate = dayjs(eventValue);
            const start = dayjs(values[0]);
            const end = dayjs(values[1]);
            return (
              (eventDate.isAfter(start) || eventDate.isSame(start)) &&
              (eventDate.isBefore(end) || eventDate.isSame(end))
            );
          } else {
            return eventValue >= values[0] && eventValue <= values[1];
          }
        }
        return false;
      }

      case 'not_between': {
        const values = String(filterValue).split(',');
        if (values.length === 2) {
          if (isDate(eventValue) && dayjs(values[0]).isValid() && dayjs(values[1]).isValid()) {
            const eventDate = dayjs(eventValue);
            const start = dayjs(values[0]);
            const end = dayjs(values[1]);
            return eventDate.isBefore(start) || eventDate.isAfter(end);
          } else {
            return eventValue < values[0] || eventValue > values[1];
          }
        }
        return false;
      }

      case 'in':
      case 'is_one_of': {
        const values = String(filterValue).split(',');
        if (isDate(eventValue)) {
          return values.some((v) => dayjs(v).isValid() && dayjs(eventValue).isSame(dayjs(v)));
        } else {
          return values.includes(String(eventValue));
        }
      }

      case 'not_in':
      case 'is_not_one_of': {
        const values = String(filterValue).split(',');
        if (isDate(eventValue)) {
          return values.every(
            (v) => !(dayjs(v).isValid() && dayjs(eventValue).isSame(dayjs(v))),
          );
        } else {
          return !values.includes(String(eventValue));
        }
      }

      default:
        return false;
    }
  });
}
